import React, { useMemo, useState } from 'react';
import { Card, CardBody, Col, Container, Button } from 'reactstrap';
import DataTable, { TableColumn } from 'react-data-table-component';
import { Link } from 'react-router-dom';
import { useIteamResource } from '../../../hook/ShowTable/generic';
import FilterComponent from '../../../Components/Tables/DataTables/Common/FilterComponent';
import Loader from '../../../Components/Widgets/Loader/generic';
import { Btn, H3 } from '../../../AbstractElements';
import DeleteConfirm from '../../../Components/Modals/Delete'


type Category = {
    ID: number | string;
    parentID: number | string | null;
    immagineHome?: string | null;
    immagineIcon?: string | null;
    immagineFS?: string | null;
    inHome?: number;
    ordine?: number;
    abilitato?: number;
    codice?: string;
    meta?: unknown;
    children?: Category[];
};

type FlatCategory = Category & { __depth: number };


type MetaI18n = Record<string, { nome?: string; permalink?: string }>;

function getNameFromMeta(meta: unknown, lang: string = 'it'): string {
    if (!meta) return '-';
    try {
        const obj: MetaI18n = typeof meta === 'string' ? JSON.parse(meta) : (meta as any);
        const byLang = obj?.[lang] || obj?.[lang.toLowerCase()] || obj?.[lang.toUpperCase()];
        return (byLang?.nome ?? '').toString() || '-';
    } catch {
        return '-';
    }
}

function flattenTree(tree: unknown, depth = 0): FlatCategory[] {
    if (!Array.isArray(tree)) return [];
    const out: FlatCategory[] = [];
    for (const node of tree as Category[]) {
        out.push({ ...(node as any), __depth: depth });
        const children = Array.isArray(node.children) ? node.children : [];
        out.push(...flattenTree(children, depth + 1));
    }
    return out;
}

function getParentName(parentID: Category['parentID'], map: Map<string | number, string>): string | null {
    if (parentID === null || parentID === undefined || parentID === 0 || parentID === '0') return null;
    return map.get(parentID as any) ?? null;
}

/* ===== Component ===== */
export default function CategoriesIndex() {
    const [filterText, setFilterText] = useState('');
    const { data: tree, loading, refetch } = useIteamResource<Category[]>('/categories/list');





    const idToName = useMemo(() => {
        const map = new Map<string | number, string>();
        const walk = (list?: Category[]) => {
            if (!Array.isArray(list)) return;
            for (const c of list) {
                map.set(c.ID as any, getNameFromMeta(c.meta, 'it'));
                if (c.children && c.children.length) walk(c.children);
            }
        };
        walk(Array.isArray(tree) ? tree : []);
        return map;
    }, [tree]);

    // Lista piatta con profondità per indentare
    const flatItems: FlatCategory[] = useMemo(
        () => flattenTree(tree ?? []),
        [tree]
    );

    // Filtro full-text (Nome, Parent, Codice, ID)
    const filteredItems: FlatCategory[] = useMemo(() => {
        if (!filterText) return flatItems;
        const q = filterText.toLowerCase();
        return flatItems.filter(item => {
            const nome = getNameFromMeta(item.meta, 'it');
            const parentName = getParentName(item.parentID, idToName) ?? '';
            return [
                nome,
                parentName,
                item.codice ?? '',
                item.ID ?? '',
                item.parentID ?? '',
                item.ordine ?? '',
            ]
                .map(v => String(v).toLowerCase())
                .some(s => s.includes(q));
        });
    }, [flatItems, filterText, idToName]);

    // Colonne tabella
    const columns: TableColumn<FlatCategory>[] = useMemo(() => [
        {
            name: 'ID',
            selector: (row) => row.ID ?? 0,
            sortable: true,
            width: '100px',
        },
        {
            name: 'Nome',
            sortable: true,
            sortFunction: (a, b) =>
                getNameFromMeta(a.meta, 'it').localeCompare(getNameFromMeta(b.meta, 'it')),
            cell: (row) => {
                const nome = getNameFromMeta(row.meta, 'it');
                return (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span style={{ display: 'inline-block', width: row.__depth * 16 }} />
                        {nome}
                    </div>
                );
            },
        },
        {
            name: 'Ordine',
            selector: (row) => row.ordine ?? 0,
            sortable: true,
            right: true,
            width: '120px',
        },
        {
            name: 'Azioni',
            width: '220px',
            button: true,
            ignoreRowClick: true,
            cell: (row) => (
                <div style={{ display: 'flex', gap: 8 }}>
                    <Button
                        size="sm"
                        color="primary"
                        tag={Link as any}
                        to={`/admin/gestioneDati/${encodeURIComponent(String(row.ID))}`}
                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    >
                        Modifica
                    </Button>
                    <DeleteConfirm
                        url={`/categories?ID=${row.ID}`}
                        onSuccess={() => {
                            refetch();
                        }}
                        trigger={
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                title="Elimina"
                            >
                                Elimina
                            </button>
                        }
                    />
                </div>
            ),
        },
    ], [idToName]);

    return (
        <Container fluid className="pt-2">
            <Col sm={12}>
                <Card>
                    <CardBody>
                        <Col xl={12} className="mb-lg-3 d-flex justify-content-between">
                            <div>
                                <H3>Gestione Dati</H3>
                            </div>
                            <Link to="/admin/gestioneDati/new">
                                <Btn className="px-4" color="primary" type="button">
                                    NUOVO
                                </Btn>
                            </Link>
                        </Col>
                        <FilterComponent
                            onFilter={(e: React.ChangeEvent<HTMLInputElement>) => setFilterText(e.target.value)}
                            filterText={filterText}
                        />
                        <div className="table-responsive">
                            <DataTable<FlatCategory>
                                data={filteredItems}
                                columns={columns}
                                progressPending={loading}
                                progressComponent={<Loader />}
                                pagination
                                striped
                                highlightOnHover
                                className="display"
                            />
                        </div>
                    </CardBody>
                </Card>
            </Col>
        </Container>
    );
}
